/**
 * Listado de plantillas de documentos.
 *
 * La acción principal de una fila es «Generar documentos»: es para lo que
 * existe la plantilla. Editar y duplicar quedan como íconos.
 */
import { useNavigate } from 'react-router-dom'
import { Copy, FileSignature, FileText, Pencil, Plus, Send, Archive } from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/Button'
import { DataTable } from '@/components/ds/DataTable'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { EmptyState } from '@/components/EmptyState'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { useToast, ToastContainer } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { formatDate, useTenantLocale } from '@/lib/locale'
import {
  useArchivarPlantilla, usePlantillas, type PlantillaDocumento,
} from '../../hooks/usePlantillasDocumentos'
import { EJEMPLOS } from './ejemplos'

const ESTADOS = {
  draft: { label: 'Borrador', tone: 'neutral' },
  active: { label: 'Activa', tone: 'success' },
  archived: { label: 'Archivada', tone: 'neutral' },
} as const

export default function PlantillasTab() {
  const navigate = useNavigate()
  const locale = useTenantLocale()
  const { can } = usePermission()
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const { data: plantillas = [], isLoading } = usePlantillas()
  const archivar = useArchivarPlantilla()
  const puedeCrear = can('hr_documents', 'create')
  const puedeEditar = can('hr_documents', 'edit')

  const nueva = (ejemplo?: string) =>
    navigate(`/payroll/documents/templates/new${ejemplo ? `?ejemplo=${ejemplo}` : ''}`)

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-content-muted max-w-2xl">
          Un texto con datos del trabajador que se genera como PDF para varias personas a la vez. Si
          su tipo de documento exige firma, cada persona la recibe para firmar desde su portal.
        </p>
        {puedeCrear && (
          <Button onClick={() => nueva()}><Plus size={14} /> Nueva plantilla</Button>
        )}
      </div>

      {!isLoading && plantillas.length === 0 ? (
        <div className="rounded-xl border border-divider bg-surface p-6">
          <EmptyState icon={FileText} size="sm" title="Todavía no hay plantillas."
            hint="Partir de un ejemplo y ajustarlo, o empezar de cero." />
          {puedeCrear && (
            <div className="grid gap-3 sm:grid-cols-2 max-w-3xl mx-auto">
              {EJEMPLOS.map((e) => (
                <button key={e.id} type="button" onClick={() => nueva(e.id)}
                  className="text-left rounded-lg border border-divider p-4 hover:bg-soft focus-visible:ring-2 ring-focus-ring">
                  <div className="font-medium text-content">{e.name}</div>
                  <div className="text-sm text-content-muted mt-1">{e.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <DataTable<PlantillaDocumento>
          rows={plantillas}
          rowKey={(p) => p.id}
          loading={isLoading}
          onRowClick={puedeEditar ? (p) => navigate(`/payroll/documents/templates/${p.id}`) : undefined}
          columns={[
            {
              key: 'name', header: 'Nombre', sortable: true, sortValue: (p) => p.name.toLowerCase(),
              render: (p) => (
                <div>
                  <div className="text-content">{p.name}</div>
                  <div className="text-xs text-content-muted">
                    {p.document_type_name}{p.folder_name ? ` · ${p.folder_name}` : ''}
                  </div>
                </div>
              ),
            },
            {
              key: 'firma', header: 'Firma',
              render: (p) => p.requires_signature
                ? <StatusBadge status="firma" tone="info" label="Pide firma" />
                : <span className="text-content-subtle">—</span>,
            },
            {
              key: 'status', header: 'Estado',
              render: (p) => <StatusBadge status={p.status} tone={ESTADOS[p.status].tone} label={ESTADOS[p.status].label} />,
            },
            {
              key: 'last', header: 'Último envío', sortable: true, sortValue: (p) => p.last_batch_at ?? '',
              render: (p) => p.last_batch_at ? formatDate(p.last_batch_at, locale) : <span className="text-content-subtle">Nunca</span>,
            },
            {
              key: 'actions', header: '',
              render: (p) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {puedeCrear && p.status === 'active' && (
                    <Button size="sm" variant="secondary"
                      onClick={() => navigate(`/payroll/documents/generate/${p.id}`)}>
                      {p.requires_signature ? <FileSignature size={14} /> : <Send size={14} />} Generar
                    </Button>
                  )}
                  {puedeEditar && (
                    <IconoAccion titulo={`Editar ${p.name}`} onClick={() => navigate(`/payroll/documents/templates/${p.id}`)}>
                      <Pencil size={14} />
                    </IconoAccion>
                  )}
                  {puedeCrear && (
                    <IconoAccion titulo={`Duplicar ${p.name}`}
                      onClick={() => navigate(`/payroll/documents/templates/new?copiar=${p.id}`)}>
                      <Copy size={14} />
                    </IconoAccion>
                  )}
                  {can('hr_documents', 'delete') && (
                    <IconoAccion titulo={`Archivar ${p.name}`} onClick={() => confirm({
                      message: p.last_batch_at
                        ? `«${p.name}» se archiva: deja de aparecer en el listado, pero sus envíos y los documentos ya generados se conservan.`
                        : `«${p.name}» se elimina. No tiene envíos.`,
                      confirmLabel: p.last_batch_at ? 'Archivar' : 'Eliminar',
                      onConfirm: () => archivar.mutateAsync(p.id)
                        .then(() => toast.success(p.last_batch_at ? 'Plantilla archivada' : 'Plantilla eliminada'))
                        .catch((e) => toast.error(extractErrorMessage(e, 'No se pudo archivar.'))),
                    })}>
                      <Archive size={14} />
                    </IconoAccion>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

function IconoAccion({ titulo, onClick, children }: { titulo: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} title={titulo} aria-label={titulo}
      className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
      {children}
    </button>
  )
}
